import React, { useRef, useEffect } from 'react';
import i18n from '@/i18n';
import Hls from 'hls.js';
import { platformClient } from '@/lib/musicApi';
import { gaEvent } from '@/lib/track';

// Adaptive video element. Uses the HLS master playlist when one exists (hls.js on
// Chrome/Firefox/Edge/Android, native HLS on Safari/iOS) and falls back to the
// plain MP4 src otherwise, so old MP4 only videos keep playing unchanged. Rendition
// switching is automatic and invisible; there is no quality picker.
//
// When the media gate is armed (hlsGated), the player first exchanges a per video
// capability for a host only edge cookie, then loads the stream with credentials so
// every segment fetch carries the cookie. If that capability is refused (session
// revoked, banned, or the video became locked) the player fails closed: it stops and
// does NOT fall back to an untokened URL. A normal expiry mid playback is refreshed
// silently by requesting a fresh capability, so playback does not die.
const IS_SAFARI = typeof navigator !== 'undefined' && /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);

// Select the audio rendition for `lang` (fallback English). hls.js or native tracks.
const pickAudio = (hls, video, lang) => {
  const want = (l) => (l || '').toLowerCase().split('-')[0];
  if (hls && hls.audioTracks && hls.audioTracks.length > 1) {
    let i = hls.audioTracks.findIndex((t) => want(t.lang) === want(lang));
    if (i < 0) i = hls.audioTracks.findIndex((t) => want(t.lang) === 'en');
    if (i >= 0 && hls.audioTrack !== i) hls.audioTrack = i;
    return;
  }
  const at = video && video.audioTracks;
  if (at && at.length > 1) {
    const list = Array.from(at);
    const target = list.find((t) => want(t.language) === want(lang)) || list.find((t) => want(t.language) === 'en');
    if (target) list.forEach((t) => { t.enabled = t === target; });
  }
};

export const HlsVideo = React.forwardRef(
  ({ hlsSrc, src, hlsGated, videoId, onPlay, onDenied, bufferSeconds = 30, audioLang, ...props }, ref) => {
    const innerRef = useRef(null);
    const hlsRef = useRef(null);
    const audioLangRef = useRef(audioLang);
    // Default: the viewer's site language (falls back to English if no such dub).
    const prefLang = audioLang || (i18n.language || 'en');
    audioLangRef.current = prefLang;
    const playedFor = useRef(null);
    // play_video once per video per mount (not on every pause/resume).
    const handlePlay = (e) => {
      if (videoId && playedFor.current !== videoId) {
        playedFor.current = videoId;
        gaEvent('play_video', { content_type: 'video', content_id: String(videoId) });
      }
      if (onPlay) onPlay(e);
    };
    const videoRef = ref || innerRef;

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return undefined;
      let hls = null;
      let cancelled = false;
      let refreshing = false;

      const stopClosed = () => {
        try { if (hls) hls.destroy(); } catch (e) { /* noop */ }
        try { video.pause(); } catch (e) { /* noop */ }
        video.removeAttribute('src');
        try { video.load(); } catch (e) { /* noop */ }
      };

      // Ask the backend for a fresh per video capability and set the edge cookie.
      // Returns the master URL, or throws when access is refused (fail closed).
      const arm = async () => {
        const { data } = await platformClient.post(`/media/hls-access/${videoId}`);
        await fetch(data.exchange_url, { method: 'GET', credentials: 'include' });
        return data.master_url || hlsSrc;
      };

      // 401/403/404 = genuine no-access. Anything else (no response, 429, 5xx) is
      // transport: retry 2s/5s/11s, then throw so the caller can wait for a revisit.
      const isDenial = (e) => [401, 403, 404].includes(e?.response?.status);
      const armWithRetry = async () => {
        for (const wait of [2000, 5000, 11000, null]) {
          try { return await arm(); } catch (e) {
            if (isDenial(e) || wait === null || cancelled) throw e;
            await new Promise((r) => setTimeout(r, wait));
            if (cancelled) throw e;
          }
        }
        return null;
      };

      // Always fail closed; a denial shows the lock screen, a transport failure
      // re-arms the next time the player comes back on screen.
      const onArmFail = (e) => {
        stopClosed();
        if (cancelled) return;
        if (isDenial(e)) { if (onDenied) onDenied(); return; }
        watch(true);
      };

      const loadWithHls = (master) => {
        hls = new Hls({
          enableWorker: true,
          maxBufferLength: bufferSeconds,
          xhrSetup: (xhr) => { xhr.withCredentials = true; },
        });
        hlsRef.current = hls;
        // Dubbed audio: pick the preferred language when the track list arrives.
        hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => pickAudio(hls, video, audioLangRef.current));
        hls.loadSource(master);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, async (_evt, data) => {
          if (!data || !data.fatal) return;
          // Gated: one silent capability refresh first, which recovers a normal
          // mid-play token expiry. arm() throwing means /media/hls-access refused,
          // i.e. the viewer is genuinely not entitled -> fail closed, no fallback.
          if (hlsGated && videoId && !refreshing) {
            refreshing = true;
            try {
              const master2 = await armWithRetry();
              if (cancelled) return;
              try { hls.destroy(); } catch (e) { /* noop */ }
              loadWithHls(master2);
              return;
            } catch (e) {
              onArmFail(e);
              return;
            }
          }
          // Ungated, or the gated refresh already ran and HLS still fails (e.g. the
          // host-only edge cookie cannot be stored cross-site, as in preview). Fall
          // back to the MP4: video_url carries its own capability token, so the edge
          // still enforces entitlement - this is not an untokened bypass.
          if (src) {
            try { hls.destroy(); } catch (e) { /* noop */ }
            video.src = src;
          } else {
            stopClosed();
          }
        });
      };

      let nativeErr = null;
      const start = async () => {
        let master = hlsSrc;
        if (hlsGated && videoId) {
          try {
            master = await armWithRetry();
          } catch (e) {
            onArmFail(e); // fail closed, no fallback
            return;
          }
          if (cancelled) return;
        }

        // Safari/iOS play HLS natively (and expose dubbed audio via video.audioTracks);
        // every other browser uses hls.js even if it claims native HLS, so the audio
        // language can always be switched.
        if (master && video.canPlayType('application/vnd.apple.mpegurl') && (IS_SAFARI || !Hls.isSupported())) {
          video.addEventListener('loadedmetadata', () => pickAudio(null, video, audioLangRef.current), { once: true });
          // Native HLS gives us no per-error hook, so listen on the element: if the
          // master/segments fail (e.g. the host-only cookie was not stored cross-site
          // in preview), fall back to the signed MP4 so the video still plays.
          nativeErr = () => {
            video.removeEventListener('error', nativeErr);
            if (src && video.currentSrc !== src) { video.src = src; }
          };
          video.addEventListener('error', nativeErr);
          video.src = master; // Safari / iOS play HLS natively (cookie is host only)
          return;
        }
        if (master && Hls.isSupported()) {
          loadWithHls(master);
          return;
        }
        if (src) video.src = src; // no HLS available -> MP4
      };

      // Load nothing until the player is on (or about to come on) screen. After a
      // transport failure, wait for it to leave and come back before trying again.
      let io = null;
      const watch = (needLeave) => {
        if (typeof IntersectionObserver === 'undefined') { if (!needLeave) start(); return; }
        let left = !needLeave;
        io = new IntersectionObserver((entries) => {
          const on = entries.some((en) => en.isIntersecting);
          if (!on) { left = true; return; }
          if (left) { io.disconnect(); io = null; refreshing = false; start(); }
        }, { rootMargin: '300px 0px' });
        io.observe(video);
      };
      watch(false);
      return () => {
        cancelled = true;
        if (io) io.disconnect();
        if (nativeErr) { try { video.removeEventListener('error', nativeErr); } catch (e) { /* noop */ } }
        try { if (hls) hls.destroy(); } catch (e) { /* noop */ }
      };
    }, [hlsSrc, src, hlsGated, videoId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Switch dubbed audio language live (no reload, position kept).
    useEffect(() => { pickAudio(hlsRef.current, videoRef.current, prefLang); }, [prefLang, videoRef]);

    return <video ref={videoRef} onPlay={handlePlay} {...props} />;
  }
);

HlsVideo.displayName = 'HlsVideo';

export default HlsVideo;
